import type { OperationVariables, TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import type { RefetchFunction } from '@apollo/client/react/hooks/useSuspenseQuery';
import classNames from 'classnames';
import { useState, type CSSProperties } from 'react';
import styled, { keyframes } from 'styled-components';

import { ManagerProgressionTaskState } from '@sorare/core/src/__generated__/globalTypes';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { DiamondPatternBackground } from '@sorare/core/src/atoms/ui/DiamondPatternBackground';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { isTypeFn } from '@sorare/core/src/lib/gql';
import {
  desktopAndAbove,
  mediumLargeDesktopAndAbove,
} from '@sorare/core/src/style/mediaQuery';

import { AnyTaskBasicClaimStory } from '#src/components/missions/AnyTaskBasicClaimStory/index.tsx';
import { HotStreaksTutorialStory } from '#src/components/series/HotStreaksTutorialStory/index.tsx';

import { useGetCardSetConfig } from '@sorare/core/src/set/index.ts';
import { SeriesMissionsWidgets } from '../SeriesMissions/SeriesMissionsWidgets/index.tsx';
import { HotStreakTile } from './HotStreakTile/index.tsx';
import { NextTrckTile } from './NextTrckTile/index.tsx';
import { SeriesBoardHeader } from './SeriesBoardHeader/index.tsx';
import type {
  SeriesBoard_cardSetRoot,
  SeriesBoard_currentUser,
} from './__generated__/index.graphql.ts';
import { DIAMOND_HEIGHT, DIAMOND_WIDTH } from './utils.ts';

const pulse = keyframes`
  0% {
    filter: blur(2px);
  }
  50% {
    filter: blur(1px) brightness(1.2);
  }
  100% {
    filter: blur(2px);
  }
`;

const Root = styled(Vertical)`
  align-items: flex-start;
  --spacing: 2;
  --row-height: calc(${DIAMOND_HEIGHT} * var(--spacing) * 1px);
  --row-width: calc(${DIAMOND_WIDTH} * (var(--spacing) + 1) * 1px);

  @media ${mediumLargeDesktopAndAbove} {
    --spacing: 3;
  }
`;

const AbsoluteHeader = styled(Vertical)`
  position: absolute;
  width: 100%;
  top: 0;
`;

const Decoration = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  background: var(--bg) no-repeat center / cover;
`;

const Content = styled(Vertical)`
  flex: 1;
  position: relative;
  width: 100%;
  padding: 100px 0;
  @media ${desktopAndAbove} {
    padding: 0;
  }
`;

const Line = styled.line`
  fill: none;
  stroke: black;
  stroke-opacity: 0.5;
  stroke-width: 6;

  &.active {
    stroke: #02dade;
    stroke-opacity: 1;
    filter: blur(2px);
    animation: ${pulse} 10s infinite;
  }
`;

const BoardContent = styled.div`
  display: flex;
  flex-direction: column-reverse;
  position: relative;
  margin: auto;
  z-index: 0;
`;

const Row = styled(Vertical)`
  height: var(--row-height);
  width: var(--row-width);
  align-items: center;
  justify-content: center;
  position: relative;
`;

const LineSvg = styled.svg`
  position: absolute;
  z-index: -1;
  bottom: 50%;
  left: ${DIAMOND_WIDTH / 2}px;
  height: var(--row-height);
  width: calc(var(--row-width) - ${DIAMOND_WIDTH}px);
`;

const MissionsWrapper = styled.div`
  align-self: flex-end;
  padding-right: var(--double-unit);

  @media ${desktopAndAbove} {
    padding-right: 0;
  }
`;

const HeaderWrapper = styled.div`
  position: relative;
  z-index: 2;
`;

type Props = {
  currentUser: SeriesBoard_currentUser;
  cardSet: SeriesBoard_cardSetRoot;
  refetch: RefetchFunction<unknown, OperationVariables>;
  switcherOpen: boolean;
  setSwitcherOpen: (switcherOpen: boolean) => void;
  showConnectTaskDialog: boolean;
  onConnectTaskDialogClose: () => void;
  onConnectTaskDialogOpen: () => void;
};

export const SeriesBoard = ({
  currentUser,
  cardSet,
  refetch,
  switcherOpen,
  setSwitcherOpen,
  showConnectTaskDialog,
  onConnectTaskDialogClose,
  onConnectTaskDialogOpen,
}: Props) => {
  const { tasksTracks } = currentUser;
  const isLaptop = useIsLaptop();
  const getCardSetConfig = useGetCardSetConfig();
  const { visual } = getCardSetConfig();
  const [openHotStreaksTutorialStory, setOpenHotStreaksTutorialStory] =
    useState(false);
  const { steps } = tasksTracks?.at(0) || {};
  const thresholdTasks = steps?.filter(isTypeFn('ThresholdPickerTask')) || [];
  const [claimedTaskId, setClaimedTaskId] = useState<string | undefined>();

  const [showTargetValue, setShowTargetValue] = useState(false);

  const claimedTask = thresholdTasks.find(task => task.id === claimedTaskId);
  const claimedTaskIndex = thresholdTasks.findIndex(
    task => task.id === claimedTaskId
  );

  const isReplay = thresholdTasks.some(task => task.replay);

  return (
    <Root>
      <Content>
        {!isLaptop && !!isReplay && (
          <Decoration
            style={
              {
                '--bg': `url(${visual.board.mobileDecoration})`,
              } as CSSProperties
            }
          />
        )}
        <DiamondPatternBackground
          width={DIAMOND_WIDTH}
          height={DIAMOND_HEIGHT}
        />
        <AbsoluteHeader>
          <HeaderWrapper>
            <SeriesBoardHeader
              trck={tasksTracks?.at(0)}
              isReplay={isReplay}
              switcherOpen={switcherOpen}
              onSwitcherToggle={() => setSwitcherOpen(!switcherOpen)}
              squad={currentUser.squad}
            />
          </HeaderWrapper>
          <MissionsWrapper>
            <SeriesMissionsWidgets
              currentUser={currentUser}
              refetch={refetch}
              cardSet={cardSet}
              showConnectTaskDialog={showConnectTaskDialog}
              onConnectTaskDialogClose={onConnectTaskDialogClose}
              onConnectTaskDialogOpen={onConnectTaskDialogOpen}
            />
          </MissionsWrapper>
        </AbsoluteHeader>
        {!!openHotStreaksTutorialStory && (
          <HotStreaksTutorialStory
            open={openHotStreaksTutorialStory}
            onClose={() => {
              setOpenHotStreaksTutorialStory(false);
            }}
            taskTrack={tasksTracks.at(0)}
          />
        )}
        <BoardContent>
          {thresholdTasks.map((task, index) => {
            const isEven = index % 2 === 0;
            const hideNextTrckLine =
              !isReplay && index === thresholdTasks.length - 1;
            return (
              <Row
                key={task.id}
                style={{
                  alignItems: index % 2 === 0 ? 'flex-start' : 'flex-end',
                }}
              >
                <HotStreakTile
                  task={task}
                  taskIndex={index}
                  showOnboarding={!!isReplay && index === 0}
                  onClaimTask={setClaimedTaskId}
                  onOpenHotStreaksTutorialStory={() => {
                    setOpenHotStreaksTutorialStory(true);
                  }}
                  showTargetValue={showTargetValue}
                  setShowTargetValue={setShowTargetValue}
                />
                {!hideNextTrckLine && (
                  <LineSvg>
                    <Line
                      className={classNames({
                        active:
                          task.aasmState ===
                          ManagerProgressionTaskState.CLAIMED,
                      })}
                      x1={isEven ? '100%' : 0}
                      y1={0}
                      x2={isEven ? 0 : '100%'}
                      y2="100%"
                    />
                  </LineSvg>
                )}
              </Row>
            );
          })}
          {!!isReplay && (
            <Row
              style={{
                alignItems:
                  thresholdTasks.length % 2 === 0 ? 'flex-start' : 'flex-end',
              }}
            >
              <NextTrckTile
                taskId={tasksTracks?.at(0)?.id || ''}
                onTrckClaimed={async () => {
                  await refetch();
                }}
                disabled={thresholdTasks.some(
                  task => task.aasmState !== ManagerProgressionTaskState.CLAIMED
                )}
              />
            </Row>
          )}
        </BoardContent>
      </Content>
      {!!claimedTask && (
        <AnyTaskBasicClaimStory
          onClose={() => {
            refetch();
            setClaimedTaskId(undefined);
          }}
          task={claimedTask}
          showContinueButton={claimedTaskIndex !== 0 || !isReplay}
        />
      )}
    </Root>
  );
};

SeriesBoard.fragments = {
  currentUser: gql`
    fragment SeriesBoard_currentUser on CurrentUser {
      slug
      squad(sport: $sport) {
        id
        joinSecret
        ...SeriesBoardHeader_squad
      }
      tasksTracks(sport: $sport, rarity: common) {
        id
        title
        iconUrl
        steps {
          id
          aasmState
          ... on ThresholdPickerTask {
            id
            replay
          }
          ...HotStreakTile_thresholdPickerTask
          ...AnyTaskBasicClaimStory_taskInterface
        }
        ...SeriesBoardHeader_tasksTrack
        ...HotStreaksTutorialStory_tasksTrack
      }
      ...SeriesMissionsWidgets_currentUser
    }
    ${HotStreakTile.fragments.thresholdPickerTask}
    ${AnyTaskBasicClaimStory.fragments.taskInterface}
    ${SeriesBoardHeader.fragments.tasksTrack}
    ${HotStreaksTutorialStory.fragments.tasksTrack}
    ${SeriesMissionsWidgets.fragments.currentUser}
    ${SeriesBoardHeader.fragments.squad}
  ` as TypedDocumentNode<SeriesBoard_currentUser>,
  cardSet: gql`
    fragment SeriesBoard_cardSetRoot on CardSetRoot {
      id
      maximumInviteTasksAsReferrer(sport: $sport)
    }
  ` as TypedDocumentNode<SeriesBoard_cardSetRoot>,
};
